import React from 'react';

import type { Media as MediaType } from '../../../payload/payload-types';
import { FeaturesBlockProps } from '@/app/_blocks/FeaturesBlockGraphQL/types';
import { Media } from '../Media';

import classes from './FeaturesBlock.module.scss';
import { DebugJsonBlock } from '../DebugJsonBlock';
import { ResimdoIcon } from '../ResimdoIcon';
import { Gutter } from '../Gutter';
import { SemanticHeading } from '../SemanticHeading';
import { renderTrademarkText } from '../TrademarkText';

const FeaturesImage = ({ image, isGrid, reverseLayout }: { image: MediaType; isGrid?: boolean; reverseLayout?: boolean; }) => {
  const layoutClass = reverseLayout ? classes.reverseLayoutGridImage : '';
  const imageClass = isGrid ? `col-6 row-2 col-md-6 row-md-2 ${classes.gridImage} ${layoutClass}` : "col-6 col-sm-12";
  return (
    <div className={imageClass}>
      <Media resource={image} alt={image.alt} className={classes.featureImage} imageSize='square' />
    </div>
  );
};

const FeaturesWrapper = ({
  hasImage,
  children,
  isGrid,
}: {
  hasImage: boolean;
  children: React.ReactNode;
  isGrid?: boolean;
}) => {
  return hasImage && !isGrid ? (
    <div className={`col-6 ${classes.featureColumn} col-sm-12`}>{children}</div>
  ) : (
    <React.Fragment>{children}</React.Fragment>
  );
};

export const FeaturesBlock: React.FC<{
  className?: string;
  featuresBlock: FeaturesBlockProps;
}> = ({ className, featuresBlock }) => {
  const isGrid: boolean = featuresBlock.layout === 'grid';
  const imageRight: boolean = featuresBlock.image?.alignment === 'right';
  const image = featuresBlock.image?.media;
  const hideDescriptionOnMobile: boolean = featuresBlock.mobileNoDescription ?? false;
  let featureClass = !!image ? '' : 'col-6 col-md-12';
  if (isGrid) {
    featureClass = `col-3 col-md-6 ${classes.gridFeature}`;
  }
  return (
    <Gutter>
      <div className={[className, classes.wrapper, 'grid'].filter(Boolean).join(' ')}>
        {!imageRight && image && <FeaturesImage image={image} isGrid={isGrid} />}
        <FeaturesWrapper hasImage={!!image} isGrid={isGrid}>
          {featuresBlock.features.map((feature, index) => (
            <div key={index} className={`${classes.featureWrapper} ${featureClass}`}>
              <div className={classes.icon}>
                <ResimdoIcon icon={feature.icon} mobileSize="50" />
              </div>
              <div className={classes.featureContent}>
                <SemanticHeading
                  heading={feature.title}
                  fallbackText={feature.title_legacy}
                  fallbackTag="h3"
                  className='heading-h3'
                />
                <div className={`${classes.featureDescription} ${hideDescriptionOnMobile ? classes.mobileHidden : ''}`}>{feature.description ? renderTrademarkText(feature.description) : null}</div>
              </div>
            </div>
          ))}
        </FeaturesWrapper>
        {imageRight && image && <FeaturesImage image={image} isGrid={isGrid} reverseLayout={true} />}
        <DebugJsonBlock block={featuresBlock} />
      </div>
    </Gutter>
  );
};

export default FeaturesBlock;
